import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Search, Tag, TrendingUp } from 'lucide-react';
import FallbackImage from '../components/FallbackImage';
import { Card, Badge, PageHero } from '../components/ui';
import { db } from '../firebase';
import { dedupeDealsByBrand } from '../utils/dedupeDeals';

const DealCardSkeleton = () => (
    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 animate-pulse">
        <div className="h-16 bg-slate-200 dark:bg-slate-700 rounded-lg mb-4"></div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mx-auto mb-2"></div>
        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mx-auto"></div>
        <div className="mt-2 h-4 bg-slate-200 dark:bg-slate-700 rounded-full w-1/3 mx-auto"></div>
    </div>
);

export default function DealsPage({ navigate }) {
    const [deals, setDeals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        const fetchDeals = async () => {
            setIsLoading(true);
            try {
                const snapshot = await getDocs(collection(db, 'dealData'));
                const dealList = snapshot.docs
                    .map(doc => ({ ...doc.data(), id: doc.id }))
                    .filter(deal => deal.discount > 0 && deal.retailerName);
                // Collapse orphaned/duplicate docs so each brand renders once.
                setDeals(dedupeDealsByBrand(dealList));
            } catch (error) {
                console.error('Error fetching deals from Firestore:', error);
                setLoadError('We had trouble loading deals. Please try again in a moment.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchDeals();
    }, []);

    const categories = useMemo(() => {
        const unique = [...new Set(deals.map(d => d.retailerCategory || 'Uncategorized'))];
        return ['All', ...unique.sort()];
    }, [deals]);

    const filteredDeals = useMemo(() => {
        let result = deals;
        if (selectedCategory !== 'All') {
            result = result.filter(deal => (deal.retailerCategory || 'Uncategorized') === selectedCategory);
        }
        if (searchTerm) {
            result = result.filter(deal =>
                deal.retailerName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return [...result].sort((a, b) => b.discount - a.discount);
    }, [deals, selectedCategory, searchTerm]);

    const handleLinkClick = (e, path) => {
        e.preventDefault();
        navigate(path);
    };

    return (
        <div className="bg-slate-50 dark:bg-slate-900 min-h-screen transition-colors duration-200">
            <PageHero
                title="Today's Best Gift Card Deals"
                subtitle="We track discounted gift cards across the web so you never pay full price at your favorite stores."
            />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 pb-16">
                <Card padded={false} className="overflow-visible">
                    <div className="p-4 sm:p-6">
                        {/* Deal count */}
                        {!isLoading && !loadError && (
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                                    <TrendingUp size={16} className="mr-1.5" />
                                    <span><strong className="text-slate-700 dark:text-slate-200">{filteredDeals.length}</strong> deals found</span>
                                </div>
                            </div>
                        )}

                        {/* Category pills */}
                        <div className="mb-4">
                            <div className="flex flex-wrap justify-center gap-2">
                                {categories.map(category => (
                                    <button
                                        key={category}
                                        onClick={() => setSelectedCategory(category)}
                                        className={`px-4 py-2 text-xs font-semibold rounded-full transition-all duration-200 ${selectedCategory === category
                                            ? 'bg-brand-700 text-white shadow-md'
                                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-brand-50 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
                                            }`}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Search */}
                        <div className="mb-6 max-w-lg mx-auto">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search retailers..."
                                    className="w-full px-4 py-3 pl-10 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                                />
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                            </div>
                        </div>

                        {/* Deal grid */}
                        {isLoading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {Array(24).fill(0).map((_, i) => <DealCardSkeleton key={i} />)}
                            </div>
                        ) : loadError ? (
                            <div className="text-center p-12 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                <p className="text-red-600 dark:text-red-400">{loadError}</p>
                            </div>
                        ) : (
                            <>
                                {filteredDeals.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {filteredDeals.map((deal) => (
                                            <Card
                                                as="a"
                                                hover
                                                key={deal.retailerId || deal.id}
                                                href={`/retailer/${deal.retailerSlug}`}
                                                onClick={(e) => handleLinkClick(e, `/retailer/${deal.retailerSlug}`)}
                                                className="flex flex-col items-center justify-center text-center relative"
                                            >
                                                {/* Hot deal ribbon */}
                                                {deal.discount >= 15 && (
                                                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-accent-500 text-white text-[10px] font-bold uppercase rounded-full shadow-sm">
                                                        🔥 Hot
                                                    </div>
                                                )}
                                                <FallbackImage
                                                    srcs={deal.retailerLogoUrls}
                                                    alt={`${deal.retailerName} logo`}
                                                    className="h-16 object-contain mb-4"
                                                />
                                                <p className="font-semibold text-slate-800 dark:text-slate-100">{deal.retailerName}</p>
                                                <p className="text-lg font-bold text-brand-700 dark:text-brand-400 mt-1">{deal.discount.toFixed(2)}% Off</p>
                                                <Badge className="mt-2" icon={Tag}>{deal.retailerCategory || 'Uncategorized'}</Badge>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center p-12 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                        <p className="text-slate-600 dark:text-slate-300">No deals found for this category or search term.</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}
